import React, { useContext, useState, useEffect, useRef } from 'react'
import { ClipLoader } from 'react-spinners'
import { AbortButton } from '../utils/Elements'
import OneClickPayment from './OneClickPayment'
import BankIDApi from '../services/api/BankID'
import DirectDebitConsent from './DirectDebitConsent'
import Context from '../context/Context'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { publicIpv4 } from 'public-ip'
import BankIDQRCode from './BankIDQRCode'
import { BankIdSigningActions } from '../helpers/Constants'

const AbortController = window.AbortController

const ResponseMessage = ({ bankIDCollectResponse, bankIDSignResponse, error }) => {
  const { t } = useTranslation()
  let response = ''

  if (error) {
    response = t('BankId Something went wrong message')
  }

  if (bankIDSignResponse.errorCode) {
    response = t(decodeURIComponent(bankIDSignResponse.errorUserMessage))
  } else {
    switch (bankIDCollectResponse.status) {
    case 'failed':
      response = t(decodeURIComponent(bankIDCollectResponse.hintCodeUserMessage))
      break
    case 'pending':
      response = t(decodeURIComponent(bankIDCollectResponse.hintCodeUserMessage))
      break
    default:
      response = ''
      break
    }
  }

  return (<p>{response}</p>)
}

const BankIDSign = ({
  textToSign,
  whatIsSigned,
  formValues,
  paymentApprovalTypeId,
  signingActionId
}) => {
  const context = useContext(Context)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [publicIp, setPublicIp] = useState(null)
  const [useQRCode, setUseQRCode] = useState(false)
  const [bankIDCollectResponse, setBankIDCollectResponse] = useState({})
  const [bankIDSignResponse, setBankIDSignResponse] = useState({})
  const isSigningInitialized = useRef(false)

  const abortController = new AbortController()

  useEffect(() => {
    if (Object.keys(bankIDSignResponse).length === 0) {
      window.scrollTo(0, 0)
      setLoading(true)
      sign(abortController.signal)
    }

    return () => null
  }, [])

  useEffect(() => {
    if (shouldCollectBeCancelled()) {
      return () => null
    }

    let intervalId = 0

    if (
      loaded &&
      loading &&
      bankIDSignResponse &&
      !bankIDSignResponse.errorCode &&
      ['complete', 'failed'].indexOf(bankIDCollectResponse.status) === -1
    ) {
      if (isSigningInitialized.current === false) {
        startSigningAction()
      }

      intervalId = setInterval(() => {
        collect(bankIDSignResponse.orderRef, abortController.signal)
      }, 3000)
    }

    if (shouldCollectBeCancelled()) {
      return () => {
        clearInterval(intervalId)
        return null
      }
    } else {
      return () => clearInterval(intervalId)
    }
  }, [bankIDSignResponse, bankIDCollectResponse])

  const startSigningAction = () => {
    if (getSigningActionId() === BankIdSigningActions.MOBILE) {
      startBankIdSigning()
    } else {
      startBankdIdQrCodeSigning()
    }
  }

  const getSigningActionId = () => {
    if (signingActionId !== undefined) {
      return signingActionId
    } else if (formValues !== undefined) {
      return parseInt(formValues.signingActionId)
    } else {
      return context.signingActionId
    }
  }

  const startBankIdSigning = () => {
    setSigningStatus(true)
    window.location = `bankid:///?autostarttoken=${bankIDSignResponse.autoStartToken}&redirect=null`
  }

  const startBankdIdQrCodeSigning = () => {
    if (bankIDSignResponse.orderRef !== undefined) {
      setSigningStatus(true)
      setUseQRCode(true)
    }
  }

  const shouldCollectBeCancelled = () => {
    if (
      Object.keys(bankIDCollectResponse).includes('status') &&
      ['complete', 'failed'].indexOf(bankIDCollectResponse.status) !== -1
    ) {
      setLoading(false)
      return true
    }

    return false
  }

  const setSigningStatus = (status) => {
    isSigningInitialized.current = status
  }

  async function sign (abortSignal) {
    const bankID = new BankIDApi()

    publicIpv4().then(ip => {
      setPublicIp(ip)
      return ip
    }).then(ip => {
      bankID.post(
        context.invoice.ocr,
        context.customer.id,
        'sign',
        ip,
        textToSign,
        abortSignal
      )
        .then(res => res.json())
        .then(data => {
          if (data.short_code >= '400') {
            setError(data)
            setLoaded(false)
          } else {
            setBankIDSignResponse(data)
            setLoaded(true)
          }
        })
        .catch(error => {
          setSigningStatus(false)
          setLoaded(false)
          setLoading(false)
          setUseQRCode(false)
          setError(error)
          abortController.abort()
        })
    }).catch(error => {
      throw new Error('Could not get public ip, try disable any ad-blockers', error)
    })
  }

  async function collect (orderRef, abortSignal) {
    const bankID = new BankIDApi()

    bankID.post(
      context.invoice.ocr,
      context.customer.id,
      'collect',
      publicIp,
      null,
      orderRef,
      abortSignal
    )
      .then(res => res.json())
      .then(data => {
        if (data.short_code >= '400') {
          setError(data)
          setLoading(false)
        } else {
          setBankIDCollectResponse(data)

          if (
            Object.keys(bankIDCollectResponse).includes('status') &&
            ['complete', 'failed'].indexOf(bankIDCollectResponse.status) !== -1
          ) {
            setUseQRCode(false)
            abortController.abort()
          }
        }
      })
      .catch(error => {
        setSigningStatus(false)
        setLoading(false)
        setUseQRCode(false)
        setError(error)
        abortController.abort()
      })
  }

  async function cancelSign () {
    const bankID = new BankIDApi()

    if (
      Object.keys(bankIDSignResponse).length > 0 &&
      bankIDSignResponse.orderRef !== null
    ) {
      bankID.cancel(
        context.customer.id,
        bankIDSignResponse.orderRef,
        abortController.signal
      )
        .then(res => res.json())
        .then(() => {
          setSigningStatus(false, true)
          setLoading(false)
          setUseQRCode(false)
          abortController.abort('User aborted sign')
        })
        .catch(error => {
          setSigningStatus(false, true)
          setUseQRCode(false)
          setLoading(false)
          setError(error)
          abortController.abort()
        })
    }
  }

  const softCancel = () => {
    setSigningStatus(false, true)
    setUseQRCode(false)
    setLoading(false)
    abortController.abort('User aborted sign')
  }

  if (bankIDCollectResponse.status === 'complete') {
    if (whatIsSigned === 'directDebitConsent') {
      return <DirectDebitConsent bankIDCollectResponse={bankIDCollectResponse} formValues={formValues} />
    } else if (whatIsSigned === 'oneClickPayment') {
      return <OneClickPayment paymentApprovalTypeId={paymentApprovalTypeId} bankIDCollectResponse={bankIDCollectResponse} />
    }
  } else {
    return (
      <div>
        {(loading && !useQRCode)
          ? <div className="row mt-5">
            <div className="col-12 text-center">
              <div className='sweet-loading'>
                <ClipLoader
                  size={75}
                  color={'#BF33BD'}
                  loading={loading}
                />
              </div>
            </div>
          </div>
          : null
        }
        <div className="row mt-3">
          <div className="col-12 text-center">
            <span className="paragraph-regular-16">
              <ResponseMessage
                error={error}
                bankIDSignResponse={bankIDSignResponse}
                bankIDCollectResponse={bankIDCollectResponse} />
            </span>
          </div>
        </div>
        {(loading && useQRCode)
          ? <div className="row mt-5">
            <div className="col-12 text-center justify-content-md-center">
              <BankIDQRCode
                orderRef={bankIDSignResponse.orderRef}
                show={useQRCode}
                onCancel={() => cancelSign()} />
            </div>
          </div>
          : null
        }
        <div className="row mt-3">
          <div className="col-12 text-center">
            {(bankIDCollectResponse.status === 'failed')
              ? <AbortButton style={context.style} url={context.url} clickAction={softCancel}/>
              : <AbortButton style={context.style} url={context.url} clickAction={cancelSign}/>
            }
          </div>
        </div>
      </div>
    )
  }
}

BankIDSign.propTypes = {
  textToSign: PropTypes.string.isRequired,
  whatIsSigned: PropTypes.string.isRequired,
  formValues: PropTypes.object,
  paymentApprovalTypeId: PropTypes.string,
  signingActionId: PropTypes.number
}

ResponseMessage.propTypes = {
  bankIDCollectResponse: PropTypes.object.isRequired,
  bankIDSignResponse: PropTypes.object.isRequired,
  error: PropTypes.object
}

export default BankIDSign
